"use client";
import { useState, useMemo, ReactNode, ChangeEvent, Ref } from "react";
import {
  AllCommunityModule,
  ModuleRegistry,
  ClientSideRowModelModule,
  RowSelectionModule,
  RowSelectionOptions,
  ColGroupDef,
  ColDef,
  SelectionChangedEvent,
  RowDataUpdatedEvent,
  GridOptions,
  RowClickedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { Form, Row, Col } from "react-bootstrap";
import ReactPaginate from "react-paginate";
import DatagridLoadingOverlay from "../../components/Datagrid/DatagridLoadingOverlay";
import { MultiValue, SingleValue } from "react-select";
import StateManagedSelect from "react-select/base";
import { Tag } from "../../components/ReactSelect";
import { useI18n, type MessagePath } from "../../i18n";

const COLUMN_HEADER_KEYS: Record<string, MessagePath> = {
  id: "fields.id",
  calendarName: "fields.calendar",
  name: "fields.name",
  description: "fields.description",
  start_date: "fields.start",
  end_date: "fields.end",
  place_id: "fields.place",
  username: "fields.username",
  role: "fields.role",
  create_date: "fields.created",
  last_update_date: "fields.lastUpdated",
  address: "fields.address",
  color: "fields.color",
  Action: "fields.action",
};

function translateColumnDefs(
  colDefs: (ColDef | ColGroupDef)[] | undefined,
  translate: (key: MessagePath) => string,
): (ColDef | ColGroupDef)[] | undefined {
  if (!colDefs) {
    return colDefs;
  }
  return colDefs.map((col) => {
    if ("children" in col || !col.field) {
      return col;
    }
    const headerKey = COLUMN_HEADER_KEYS[col.field];
    if (!headerKey) {
      return col;
    }
    return { ...col, headerName: translate(headerKey) };
  });
}

ModuleRegistry.registerModules([
  AllCommunityModule,
  RowSelectionModule,
  ClientSideRowModelModule,
]);

function DataGridControls({
  leftElementControl,
  rightButtonMenu,
}: {
  leftElementControl: ReactNode;
  rightButtonMenu: ReactNode;
}) {
  return (
    <>
      <Row className="mb-2" style={{ paddingRight: 0 }}>
        {leftElementControl && <Col>{leftElementControl}</Col>}
        {rightButtonMenu && <Col>{rightButtonMenu}</Col>}
      </Row>
    </>
  );
}

function DatagridSelectionCount({
  selectionCount,
}: {
  selectionCount: number;
}) {
  return (
    <Row className="mb-1">
      <Col>
        <b>{selectionCount} row(s) selected</b>
      </Col>
    </Row>
  );
}

type DatagridPaginationProps = {
  hasPagination: boolean;
  paginatePageCount: number;
  paginatePageSizeArray: number[];
  handlePageClick?: (selectedItem: { selected: number }) => void;
  handlePageSizeChange?: (event: ChangeEvent<HTMLSelectElement>) => void;
  paginatePage: number;
};

function DatagridPagination({
  hasPagination,
  paginatePageCount,
  paginatePageSizeArray,
  handlePageClick,
  handlePageSizeChange,
  paginatePage,
}: DatagridPaginationProps) {
  const { t } = useI18n();
  const defaultPageSizeArray = [10, 20, 50, 100, 150, 200];
  const pageSizeArray =
    Array.isArray(paginatePageSizeArray) && paginatePageSizeArray.length > 0
      ? paginatePageSizeArray
      : defaultPageSizeArray;
  const [pageSize, setPageSize] = useState(pageSizeArray[0]);
  const pageSizeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    if (
      !event ||
      !event?.target ||
      event?.target?.value == null ||
      Number.isNaN(event?.target?.value)
    ) {
      return;
    }
    setPageSize(Number(event.target.value));
    if (typeof handlePageSizeChange === "function") {
      handlePageSizeChange(event);
    }
  };

  return (
    <>
      {hasPagination && (
        <>
          <Row className={"mt-2"}>
            <Col>
              <div style={{ float: "right" }}>
                {paginatePageCount > 0 && (
                  <Form.Select
                    className={"d-inline mx-2"}
                    style={{ width: "unset" }}
                    onChange={pageSizeChange}
                    value={pageSize.toString()}
                  >
                    {pageSizeArray.map((x) => {
                      return (
                        <option key={x} value={x}>
                          {x}
                        </option>
                      );
                    })}
                  </Form.Select>
                )}
                <div className={"d-inline-grid"}>
                  {paginatePageCount > 0 && (
                    <ReactPaginate
                      nextLabel={t("grid.paginationNext")}
                      onPageChange={handlePageClick}
                      pageRangeDisplayed={4}
                      marginPagesDisplayed={1}
                      pageCount={paginatePageCount}
                      previousLabel={t("grid.paginationPrevious")}
                      pageClassName="page-item"
                      pageLinkClassName="page-link"
                      previousClassName="page-item"
                      previousLinkClassName="page-link"
                      nextClassName="page-item"
                      nextLinkClassName="page-link"
                      breakLabel="..."
                      breakClassName="page-item"
                      breakLinkClassName="page-link"
                      containerClassName="pagination m-0"
                      activeClassName="active"
                      renderOnZeroPageCount={null}
                      forcePage={paginatePage}
                    />
                  )}
                </div>
              </div>
            </Col>
          </Row>
        </>
      )}
    </>
  );
}

type DatagridProps = {
  rowSelect?: string;
  gridRef?: Ref<AgGridReact>;
  rowData?: any[];
  colDefs?: (ColDef | ColGroupDef)[];
  height?: string;
  windowHeight?: number;
  rowHeight?: number;

  onSelectionChanged?: (event: SelectionChangedEvent) => void;
  onRowDataUpdated?: (event: RowDataUpdatedEvent) => void;
  customGridOptions?: GridOptions;
  onRowClicked?: (event: RowClickedEvent) => void;

  //DataGridControls
  hasControls?: boolean;
  topLeftElement?: ReactNode;
  topRightElement?: ReactNode;
  hasFilter?: boolean;

  //DatagridPagination
  // hasPagination?: boolean;
  // handlePageClick?: any;
  // handlePageSizeChange?: any;
  // paginatePage?: number;
  // paginatePageCount?: number;
  // paginatePageSizeArray?: number[];

  hasPagination?: boolean;
  paginatePageCount?: number;
  paginatePageSizeArray?: number[];
  handlePageClick?: (selectedItem: { selected: number }) => void;
  handlePageSizeChange?: (event: ChangeEvent<HTMLSelectElement>) => void;
  paginatePage?: number;
  loading?: boolean;

  //DatagridSelectionCount
  searchValue?: string;
  searchBarWidth?: string;
  tagSelectorRef?: Ref<StateManagedSelect>;
  filterOptions?: Tag[];
  filterOnChange?: (value: MultiValue<Tag> | SingleValue<Tag>) => void;
  filterPlaceholder?: string;
  hasSelectionCount?: boolean;
  selectionCount?: number;
  searchOnChangeCustom?: (event: ChangeEvent<HTMLInputElement>) => void;

  noRowsOverlayComponent?: any;
};

const gridHeight = {
  allControls: {
    sm: "70vh",
    md: "76vh",
  },
  noControls: {
    sm: "75vh",
    md: "80vh",
  },
  pagination: {
    sm: "72vh",
    md: "78vh",
  },
};

function getGridHeight(
  height?: number,
  hasSearch = false,
  hasPagination = false,
) {
  if (height) {
    if (hasSearch === true && hasPagination === true) {
      return height < 800
        ? gridHeight?.allControls?.sm
        : gridHeight?.allControls?.md;
    }
    if (hasSearch !== true && hasPagination !== true) {
      return height < 800
        ? gridHeight?.noControls?.sm
        : gridHeight?.noControls?.md;
    }
    if (hasSearch !== true && hasPagination === true) {
      return height < 800
        ? gridHeight?.pagination?.sm
        : gridHeight?.pagination?.md;
    }
  }
  return gridHeight?.allControls?.sm;
}

export default function Datagrid({
  rowSelect = "singleRow",
  gridRef,
  rowData,
  colDefs,
  height,
  windowHeight,
  rowHeight = 45,
  onSelectionChanged,
  onRowDataUpdated,
  customGridOptions = {},
  onRowClicked,

  //DataGridControls
  hasControls = true,
  topLeftElement = null,
  topRightElement = null,
  hasFilter = false,
  loading = false,

  //DatagridPagination
  hasPagination = false,
  handlePageClick = undefined,
  handlePageSizeChange = undefined,
  paginatePage = 1,
  paginatePageCount = 10,
  paginatePageSizeArray = [],

  //DatagridSelectionCount
  hasSelectionCount = false,
  selectionCount = -1,

  searchOnChangeCustom,

  filterOptions = undefined,
  filterOnChange = undefined,
  filterPlaceholder = undefined,
  searchBarWidth = `300px`,

  noRowsOverlayComponent = null,
}: DatagridProps) {
  const { locale, t } = useI18n();
  const translatedColDefs = useMemo(
    () => translateColumnDefs(colDefs, t),
    [colDefs, locale, t],
  );
  const defaultColDef = {
    sortable: false,
  };

  const rowSelection = useMemo(() => {
    return {
      mode: rowSelect, //"multiRow",
      enableClickSelection: true,
      enableSelectionWithoutKeys: true,
      checkboxes: true,
    } as RowSelectionOptions | "single" | "multiple";
  }, [rowSelect]);

  const leftElementControl = topLeftElement != null ? topLeftElement : <></>;
  return (
    <>
      {hasControls && (
        <DataGridControls
          leftElementControl={leftElementControl}
          rightButtonMenu={topRightElement}
        />
      )}
      {hasSelectionCount && (
        <DatagridSelectionCount selectionCount={selectionCount} />
      )}
      <div
        style={{
          height:
            height ?? getGridHeight(windowHeight, hasControls, hasPagination),
        }}
      >
        <AgGridReact
          ref={gridRef}
          columnDefs={translatedColDefs}
          rowData={rowData}
          defaultColDef={defaultColDef}
          suppressCellFocus={true}
          rowHeight={rowHeight}
          enableCellTextSelection={true}
          rowSelection={rowSelection}
          onSelectionChanged={onSelectionChanged}
          onRowDataUpdated={onRowDataUpdated}
          gridOptions={customGridOptions}
          noRowsOverlayComponent={noRowsOverlayComponent}
          onRowClicked={onRowClicked}
          loadingOverlayComponent={DatagridLoadingOverlay}
          loading={loading}
        />
      </div>
      <DatagridPagination
        hasPagination={hasPagination}
        paginatePageCount={paginatePageCount}
        paginatePageSizeArray={paginatePageSizeArray}
        handlePageClick={handlePageClick}
        handlePageSizeChange={handlePageSizeChange}
        paginatePage={paginatePage}
      />
    </>
  );
}
